// components/common/Journey/Journey.tsx
import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/common/Button/Button";
import Modal from "../../../components/common/Modal/Modal";
import { notify } from "../../../components/common/Toast/toastBridge";
import { useAuth } from "../../../contexts/AuthContext";
import { User } from "../../../Entities";
import JOURNEY_STEPS from "./JourneySteps";
import SafetyNoticeModal from "./SafetyNoticeModal";
import { SAFETY_NOTICE_TITLE, SAFETY_NOTICE_INTRO } from "./journeySafetyNotice";
import styles from "./Journey.module.css"

interface JourneyProps {
	/** Zavolá se ve chvíli, kdy uživatel dokončí poslední zbývající krok. */
	onAllDone?: () => void;
}

const Journey = ({ onAllDone }: JourneyProps) => {
	const { user, setUser } = useAuth();
	const navigate = useNavigate();

	const [doneStepIds, setDoneStepIds] = useState<number[]>(user!.journey_done_step_ids ?? []);
	const [processing, setProcessing] = useState<boolean>(false);
	const [expandedId, setExpandedId] = useState<string | number | null>(null);
	const [showCompleteModal, setShowCompleteModal] = useState(false);

	// Kritické bezpečnostní upozornění NENÍ krok cesty a nikam se neukládá –
	// jde jen o dočasný UI stav, který vynucuje jeho přečtení před skrytím.
	const [showSafetyNotice, setShowSafetyNotice] = useState(false);
	const [safetyNoticeMode, setSafetyNoticeMode] = useState<"view" | "gate">("view");

	const wasAllDone = useRef<boolean>(doneStepIds.length >= JOURNEY_STEPS.length);

	const isPreview = window.location.pathname.startsWith('/preview/dashboard');
	const dashboardPath = isPreview ? '/preview/dashboard' : '/dashboard';

	const toggle = async (id: number): Promise<void> => {
		try {
			if (!user) return;
			setProcessing(true);

			const newDoneIds = doneStepIds.includes(id) ? doneStepIds.filter(i => i !== id) : [...doneStepIds, id];

			let updatedUser = new User(user);
			updatedUser.journey_done_step_ids = newDoneIds;
			await updatedUser.update();

			setDoneStepIds(newDoneIds);
			setUser(updatedUser);

			const nowAllDone = newDoneIds.length >= JOURNEY_STEPS.length;
			if (nowAllDone && !wasAllDone.current) {
				setShowCompleteModal(true);
				onAllDone?.();
			}
			wasAllDone.current = nowAllDone;

		} catch (e: any) {
			console.error(e);
			notify.error('Nepodařilo se zaznamenat změnu');
		} finally {
			setProcessing(false);
		}
	};

	const toggleExpanded = useCallback((id: string | number) => {
		setExpandedId((prev) => (prev === id ? null : id));
	}, []);

	// Skutečné skrytí sekce – volá se až POTÉ, co uživatel potvrdí, že si
	// přečetl bezpečnostní upozornění (viz requestHide / handleSafetyConfirm).
	const hideSection = async () => {
		try {
			if (!user) return;
			setProcessing(true);

			let updatedUser = new User(user);
			updatedUser.journey_done_step_ids = null;
			await updatedUser.update();

			setUser(updatedUser);
			setShowCompleteModal(false);
			navigate(dashboardPath);

		} catch (e: any) {
			console.error(e);
			notify.error("Nepodařilo se skrýt Začínáme");
		} finally {
			setProcessing(false);
		}

	};

	// Vstupní bod pro uživatele (tlačítko v hlavičce i v gratulační modalce).
	// Vždy nejdřív otevře gate s bezpečnostním upozorněním – nikdy neskrývá napřímo.
	const requestHide = () => {
		setShowCompleteModal(false);
		setSafetyNoticeMode("gate");
		setShowSafetyNotice(true);
	};

	const openSafetyNoticeView = () => {
		setSafetyNoticeMode("view");
		setShowSafetyNotice(true);
	};

	const handleSafetyConfirm = () => {
		setShowSafetyNotice(false);
		void hideSection();
	};

	const pct = JOURNEY_STEPS.length ? Math.round((doneStepIds.length / JOURNEY_STEPS.length) * 100) : 0;
	const allDone = JOURNEY_STEPS.length > 0 && doneStepIds.length === JOURNEY_STEPS.length;

	return (
		<div className={styles.journey}>
			<section className={styles.card}>
				<div className={styles.header}>
					<div>
						<h2 className={styles.title}>Tvoje cesta</h2>
						<p className={styles.subtitle}>
							Odškrtávej si postupně jednotlivé kroky, jak jimi procházíš.
						</p>
					</div>
					<div className={styles.headerRight}>
						<div className={styles.counter}>
							<span className={styles.counterNum}>
								{doneStepIds.length}/{JOURNEY_STEPS.length}
							</span>
							<span className={styles.counterLabel}>hotovo</span>
						</div>
						<button
							type="button"
							className={styles.hideBtn}
							onClick={requestHide}
						>
							Skrýt začínáme
						</button>
					</div>
				</div>

				<div className={styles.progressTrack} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
					<div className={styles.progressFill} style={{ width: `${pct}%` }} />
				</div>

				<div className={styles.noticeBanner} role="note">
					<span className={styles.noticeBannerIcon} aria-hidden="true">⚠️</span>
					<div className={styles.noticeBannerBody}>
						<p className={styles.noticeBannerTitle}>{SAFETY_NOTICE_TITLE}</p>
						<p className={styles.noticeBannerText}>{SAFETY_NOTICE_INTRO}</p>
						<button type="button" className={styles.noticeBannerLink} onClick={openSafetyNoticeView}>
							Zobrazit celé upozornění
						</button>
					</div>
				</div>

				<ol className={styles.steps}>
					{JOURNEY_STEPS.map((step, i) => {
						const isDone = !!doneStepIds.includes(step.id);
						const isExpanded = expandedId === step.id;
						const hasDetail = !!step.detail?.length;
						return (
							<li key={step.id} className={`${styles.step} ${isDone ? styles.stepDone : ''} ${isExpanded ? styles.stepExpanded : ''}`}>
								<div className={styles.stepRow}>
									<button
										className={styles.checkbox}
										onClick={() => toggle(step.id)}
										aria-pressed={isDone}
										disabled={processing}
										aria-label={`${isDone ? 'Zrušit označení' : 'Označit jako splněné'}: ${step.title}`}
									>
										{isDone ? '✓' : i + 1}
									</button>
									<button
										className={styles.stepBody}
										onClick={() => hasDetail && toggleExpanded(step.id)}
										aria-expanded={hasDetail ? isExpanded : undefined}
										disabled={!hasDetail}
									>
										<span className={styles.stepTitle}>{step.title}</span>
										<span className={styles.stepDesc}>{step.description}</span>
									</button>
									{hasDetail && (
										<span className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`} aria-hidden="true">
											⌄
										</span>
									)}
								</div>
								{hasDetail && isExpanded && (
									<ul className={styles.detail}>
										{step.detail!.map((line, j) => (
											<li key={j}>{line}</li>
										))}
									</ul>
								)}
							</li>
						);
					})}
				</ol>

				{allDone && (
					<div className={styles.doneBanner}>🎉 Skvělá práce — prošel jsi celou cestu!</div>
				)}
			</section>

			<Modal
				isOpen={showCompleteModal}
				onClose={() => setShowCompleteModal(false)}
				title="Gratulujeme! 🎉"
				size="small"
			>
				<div className={styles.completeModal}>
					<p>
						Gratulujeme k úspěšnému startu s DigiBrigi. Chcete skrýt sekci Začínáme?
					</p>
					<div className={styles.completeActions}>
						<Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
							Ponechat
						</Button>
						<Button variant="primary" onClick={requestHide}>
							Skrýt
						</Button>
					</div>
				</div>
			</Modal>

			<SafetyNoticeModal
				isOpen={showSafetyNotice}
				onClose={() => setShowSafetyNotice(false)}
				mode={safetyNoticeMode}
				onConfirm={handleSafetyConfirm}
			/>
		</div>
	);
};

export default Journey;